/* ═══════════════════════════════════════════════════════════════
   FilterPanel — unified filter popover.

   Sections:
     1. People scope (Mine / My team / My projects / All) — preset
        shortcuts that narrow the visible set without touching chips.
        Disabled options grey out; the user can't pick a scope they
        don't have data for.
     2. Departments — multi-select chip picker with search.
     3. Teams — multi-select chip picker with search (NEW).
     4. Projects — multi-select chip picker with search.

   All four sections combine AND-ly: scope first, then chips narrow
   further. "Clear all" resets every section back to defaults.
   ═══════════════════════════════════════════════════════════════ */

import React, { useEffect, useRef, useState } from 'react';
import type { Department, Project } from '../../../shared/types';
import type { Team } from '../../../shared/entities';
import { type Scope, type ViewConfig, btnGhost, projectColor, textInput } from './_shared';

export function FilterPanel({
  departments,
  teams,
  projects,
  config,
  onConfigChange,
  scopeOptions,
  onClose,
}: {
  departments: Department[];
  teams: Team[];
  projects: Project[];
  config: ViewConfig;
  onConfigChange: (next: ViewConfig) => void;
  scopeOptions: { value: Scope; label: string; available: boolean }[];
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [projectQuery, setProjectQuery] = useState('');
  const [teamQuery, setTeamQuery] = useState('');
  useEffect(() => {
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    setTimeout(() => document.addEventListener('mousedown', handle), 0);
    return () => document.removeEventListener('mousedown', handle);
  }, [onClose]);

  // Search filters for the chip pickers. Selected items always stay
  // visible so the user can deselect them even when their query would
  // otherwise hide them.
  const pq = projectQuery.toLowerCase().trim();
  const filteredProjects = pq
    ? projects.filter(
        (p) =>
          config.selectedProjectIds.includes(p.id) ||
          p.name.toLowerCase().includes(pq) ||
          (p.projectNumber || '').toLowerCase().includes(pq)
      )
    : projects;
  const tq = teamQuery.toLowerCase().trim();
  const filteredTeams = tq
    ? teams.filter(
        (t) =>
          config.selectedTeamIds.includes(t.id) ||
          t.name.toLowerCase().includes(tq) ||
          (t.shortCode || '').toLowerCase().includes(tq)
      )
    : teams;

  const sectionHead: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 700,
    color: 'var(--tm)',
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: '.05em',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  };

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label="Forecast filters"
      style={{
        position: 'absolute',
        top: 60,
        right: 16,
        background: 'var(--sf)',
        border: '1px solid var(--bd)',
        borderRadius: 8,
        boxShadow: '0 8px 24px rgba(0,0,0,.16)',
        padding: 16,
        minWidth: 360,
        maxWidth: 400,
        maxHeight: '70vh',
        overflow: 'auto',
        zIndex: 10,
      }}
    >
      {/* ─── Section 1: People scope ─── */}
      <div style={sectionHead}>
        <span>People</span>
      </div>
      <div
        role="radiogroup"
        aria-label="People scope"
        style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 16 }}
      >
        {scopeOptions.map((opt) => {
          const active = config.scope === opt.value;
          const disabled = !opt.available;
          return (
            <button
              key={opt.value}
              role="radio"
              aria-checked={active}
              disabled={disabled}
              onClick={() => onConfigChange({ ...config, scope: opt.value })}
              title={
                disabled
                  ? `${opt.label} — no data available for you`
                  : `Show ${opt.label.toLowerCase()}`
              }
              style={{
                padding: '5px 12px',
                borderRadius: 999,
                border: `1px solid ${active ? 'var(--ac)' : 'var(--bd)'}`,
                background: active ? 'color-mix(in srgb, var(--ac) 16%, transparent)' : 'var(--sa)',
                color: disabled ? 'var(--tm)' : active ? 'var(--ac)' : 'var(--tx)',
                fontSize: 12,
                fontWeight: active ? 700 : 500,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1,
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      {/* ─── Section 2: Departments ─── */}
      <div style={sectionHead}>
        <span>
          Departments{' '}
          {config.selectedDepartmentIds.length > 0 && (
            <span style={{ color: 'var(--ac)' }}>
              ({config.selectedDepartmentIds.length} selected)
            </span>
          )}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 16 }}>
        {departments.length === 0 && (
          <div style={{ padding: '8px 4px', fontSize: 11, color: 'var(--tm)' }}>
            No departments configured.
          </div>
        )}
        {departments.map((d) => {
          const active = config.selectedDepartmentIds.includes(d.id);
          return (
            <button
              key={d.id}
              onClick={() => {
                const next = active
                  ? config.selectedDepartmentIds.filter((id) => id !== d.id)
                  : [...config.selectedDepartmentIds, d.id];
                onConfigChange({ ...config, selectedDepartmentIds: next });
              }}
              aria-pressed={active}
              style={{
                padding: '4px 10px',
                borderRadius: 999,
                border: `1px solid ${active ? d.color || 'var(--ac)' : 'var(--bd)'}`,
                background: active
                  ? `color-mix(in srgb, ${d.color || 'var(--ac)'} 16%, transparent)`
                  : 'var(--sa)',
                color: active ? d.color || 'var(--ac)' : 'var(--tx)',
                fontSize: 11,
                fontWeight: active ? 700 : 500,
                cursor: 'pointer',
              }}
              title={d.name}
            >
              {d.abbreviation || d.name}
            </button>
          );
        })}
      </div>

      {/* ─── Section 3: Teams (NEW) ─── */}
      <div style={sectionHead}>
        <span>
          Teams{' '}
          {config.selectedTeamIds.length > 0 && (
            <span style={{ color: 'var(--ac)' }}>({config.selectedTeamIds.length} selected)</span>
          )}
        </span>
      </div>
      {teams.length > 0 && (
        <input
          value={teamQuery}
          onChange={(e) => setTeamQuery(e.target.value)}
          placeholder="Search teams…"
          aria-label="Search teams"
          style={{ ...textInput, fontSize: 12, padding: '5px 8px', marginBottom: 6 }}
        />
      )}
      <div
        style={{
          display: 'flex',
          gap: 4,
          flexWrap: 'wrap',
          maxHeight: 160,
          overflow: 'auto',
          padding: 2,
          marginBottom: 16,
        }}
      >
        {teams.length === 0 && (
          <div style={{ padding: '8px 4px', fontSize: 11, color: 'var(--tm)' }}>
            No teams configured. Create one in Team → Teams.
          </div>
        )}
        {filteredTeams.length === 0 && teams.length > 0 && (
          <div style={{ padding: '8px 4px', fontSize: 11, color: 'var(--tm)' }}>
            No matching teams.
          </div>
        )}
        {filteredTeams.map((t) => {
          const active = config.selectedTeamIds.includes(t.id);
          const color = t.color || 'var(--ac)';
          return (
            <button
              key={t.id}
              onClick={() => {
                const next = active
                  ? config.selectedTeamIds.filter((id) => id !== t.id)
                  : [...config.selectedTeamIds, t.id];
                onConfigChange({ ...config, selectedTeamIds: next });
              }}
              aria-pressed={active}
              style={{
                padding: '4px 10px',
                borderRadius: 999,
                border: `1px solid ${active ? color : 'var(--bd)'}`,
                background: active ? `color-mix(in srgb, ${color} 16%, transparent)` : 'var(--sa)',
                color: active ? color : 'var(--tx)',
                fontSize: 11,
                fontWeight: active ? 700 : 500,
                cursor: 'pointer',
              }}
              title={t.name}
            >
              {t.shortCode ? `${t.shortCode} · ${t.name}` : t.name}
            </button>
          );
        })}
      </div>

      {/* ─── Section 4: Projects ─── */}
      <div style={sectionHead}>
        <span>
          Projects{' '}
          {config.selectedProjectIds.length > 0 && (
            <span style={{ color: 'var(--ac)' }}>
              ({config.selectedProjectIds.length} selected)
            </span>
          )}
        </span>
      </div>
      <input
        value={projectQuery}
        onChange={(e) => setProjectQuery(e.target.value)}
        placeholder="Search projects…"
        aria-label="Search projects"
        style={{ ...textInput, fontSize: 12, padding: '5px 8px', marginBottom: 6 }}
      />
      <div
        style={{
          display: 'flex',
          gap: 4,
          flexWrap: 'wrap',
          maxHeight: 220,
          overflow: 'auto',
          padding: 2,
        }}
      >
        {filteredProjects.length === 0 && (
          <div style={{ padding: '12px 4px', fontSize: 11, color: 'var(--tm)' }}>
            No matching projects.
          </div>
        )}
        {filteredProjects.map((p) => {
          const active = config.selectedProjectIds.includes(p.id);
          return (
            <button
              key={p.id}
              onClick={() => {
                const next = active
                  ? config.selectedProjectIds.filter((id) => id !== p.id)
                  : [...config.selectedProjectIds, p.id];
                onConfigChange({ ...config, selectedProjectIds: next });
              }}
              aria-pressed={active}
              style={{
                padding: '4px 10px',
                borderRadius: 999,
                border: `1px solid ${active ? projectColor(p.id) : 'var(--bd)'}`,
                background: active
                  ? `color-mix(in srgb, ${projectColor(p.id)} 16%, transparent)`
                  : 'var(--sa)',
                color: active ? projectColor(p.id) : 'var(--tx)',
                fontSize: 10,
                fontWeight: active ? 700 : 500,
                cursor: 'pointer',
              }}
              title={p.name}
            >
              {p.projectNumber ? `${p.projectNumber} ` : ''}
              {p.name.length > 22 ? p.name.slice(0, 22) + '…' : p.name}
            </button>
          );
        })}
      </div>

      {/* ─── Footer: Clear all ─── */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          justifyContent: 'flex-end',
          marginTop: 16,
          paddingTop: 12,
          borderTop: '1px solid var(--bd)',
        }}
      >
        <button
          onClick={() =>
            onConfigChange({
              ...config,
              scope: 'all',
              selectedDepartmentIds: [],
              selectedTeamIds: [],
              selectedProjectIds: [],
            })
          }
          style={btnGhost}
        >
          Clear all
        </button>
      </div>
    </div>
  );
}
